import { useEffect, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  api,
  type ApiError,
  type DocumentTemplatesResult,
  type GetQuoteTemplateResult,
  type ListProductsResult,
} from '@whinerp/client';
import { formatMoney } from '@whinerp/contracts/format';
import {
  Button,
  CapabilityUnavailable,
  ErrorState,
  LineItemGrid,
  Num,
  PageHeading,
  Panel,
  SectionLabel,
  Skeleton,
  gridTotals,
  lineTotalOf,
  type GridLine,
} from '@whinerp/ui';
import { useErrorMessage, useFormat, useT } from '../../app/i18n.js';
import { useEntitlement, useSession } from '../../app/session.js';
import { defineScreen } from '../types.js';

/**
 * SC-43 Quotation template builder (F-158).
 *
 * The quote builder's own grid and preview, so a template looks while it is
 * being built exactly like the quote it will start. Saving writes a new
 * version; an earlier version opens read-only, because it is a record of what
 * quotes were started from, not a draft.
 *
 * Product lines say, where they are added, that they are priced from the
 * catalogue when the template is used. A price typed on a product line that
 * silently became a different price on the quote would be a label lying.
 */

type Template = GetQuoteTemplateResult;

const INPUT = 'h-input rounded-control border border-control bg-surface px-3 text-body text-ink';

const BLANK_LINE: GridLine = {
  lineId: null,
  productId: null,
  description: '',
  quantity: '1',
  unitPrice: '0.00',
  discountPercent: '0',
  optional: false,
};

function QuoteTemplateBuilderScreen(): JSX.Element {
  const t = useT();
  const errorText = useErrorMessage();
  const format = useFormat();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const params = useParams();
  const session = useSession();
  const templating = useEntitlement('F-158');
  const creating = (params['id'] ?? 'new') === 'new';
  const templateId = creating ? null : (params['id'] ?? null);
  const manage = session.can('quote.template.manage');

  const [viewing, setViewing] = useState<number | null>(null);
  const [name, setName] = useState('');
  const [currency, setCurrency] = useState(session.session.workspace.currency);
  const [validityDays, setValidityDays] = useState('30');
  const [terms, setTerms] = useState('');
  const [documentTemplateId, setDocumentTemplateId] = useState('');
  const [lines, setLines] = useState<GridLine[]>([BLANK_LINE]);
  const [product, setProduct] = useState('');
  const [search, setSearch] = useState('');

  const template = useQuery<Template, ApiError>({
    queryKey: ['quote-template', templateId, viewing],
    queryFn: () =>
      api.getQuoteTemplate({
        path: { templateId: templateId ?? '' },
        query: viewing === null ? {} : { version: viewing },
      }),
    enabled: templateId !== null && templating.enabled,
    // Switching version must not unmount the version selector mid-use.
    placeholderData: keepPreviousData,
  });

  const documents = useQuery<DocumentTemplatesResult, ApiError>({
    queryKey: ['document-templates', 'quote'],
    queryFn: () => api.documentTemplates({ query: { objectKey: 'quote' } }),
    enabled: templating.enabled && manage,
  });

  const products = useQuery<ListProductsResult, ApiError>({
    queryKey: ['products', 'template-builder', search],
    queryFn: () => api.listProducts({ query: search.trim() ? { search } : {} }),
    enabled: templating.enabled && manage,
  });

  useEffect(() => {
    const data = template.data;
    if (!data) return;
    setName(data.name);
    setCurrency(data.currency);
    setValidityDays(String(data.validityDays));
    setTerms(data.terms ?? '');
    setDocumentTemplateId(data.documentTemplateId ?? '');
    setLines(
      data.lines.map((line) => ({
        lineId: null,
        productId: line.productId,
        description: line.description,
        quantity: line.quantity,
        unitPrice: line.unitPrice,
        discountPercent: line.discountPercent,
        optional: line.optional,
      })),
    );
  }, [template.data]);

  const save = useMutation<Template, ApiError>({
    mutationFn: () =>
      api.saveQuoteTemplate({
        body: {
          templateId,
          expectedVersion: template.data?.version ?? null,
          name,
          currency,
          validityDays: Number(validityDays),
          terms: terms.trim() === '' ? null : terms,
          documentTemplateId: documentTemplateId === '' ? null : documentTemplateId,
          lines: lines.map((line) => ({
            productId: line.productId,
            description: line.description,
            quantity: line.quantity,
            unitPrice: line.unitPrice,
            discountPercent: line.discountPercent,
            optional: line.optional,
          })),
        },
      }),
    onSuccess: async (saved) => {
      queryClient.setQueryData(['quote-template', saved.templateId, null], saved);
      await queryClient.invalidateQueries({ queryKey: ['quote-templates'] });
      if (creating) navigate(`/quote-templates/${saved.templateId}`, { replace: true });
    },
  });

  const archive = useMutation<unknown, ApiError>({
    mutationFn: () => api.archiveQuoteTemplate({ path: { templateId: templateId ?? '' }, body: {} }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['quote-templates'] });
      navigate('/setup#quote-templates');
    },
  });

  if (templating.state === 'off' || templating.state === 'plan-locked') {
    return (
      <CapabilityUnavailable
        kind={templating.state === 'off' ? 'module-off' : 'plan-locked'}
        capability={t('quoteTemplate.title')}
        title={t('capability.moduleOff.title')}
        body={t('capability.moduleOff.body', { values: { capability: t('quoteTemplate.title') } })}
        action={{ label: t('capability.moduleOff.action'), href: '/setup/modules' }}
      />
    );
  }
  if (template.isPending && templateId !== null) return <QuoteTemplateLoading />;
  if (template.isError) {
    return <ErrorState title={t('state.error')} body={errorText(template.error)} onRetry={() => void template.refetch()} />;
  }

  const current = template.data;
  const oldVersion = viewing !== null && current !== undefined && viewing !== current.versions[0]?.version;
  const editable = manage && !oldVersion;
  const money = (amount: string): string => formatMoney(amount, format, { currency });
  const totals = gridTotals(lines, '0');

  const addProduct = (productId: string): void => {
    const picked = products.data?.products.find((entry) => entry.productId === productId);
    if (!picked) return;
    setLines((existing) => [
      ...existing.filter((line) => line.description.trim() !== '' || line.productId !== null),
      { ...BLANK_LINE, productId: picked.productId, description: picked.name, unitPrice: picked.listPrice },
    ]);
    setProduct('');
  };

  return (
    <div data-component="QuoteTemplateBuilder" className="flex w-full flex-col gap-4">
      <header className="flex flex-wrap items-center gap-3">
        <PageHeading>{creating ? t('quoteTemplate.new') : name || t('quoteTemplate.title')}</PageHeading>
        {/* One version is a fact, not a choice: a select with one option changes nothing. */}
        {current && current.versions.length === 1 ? (
          <span data-template-version-only className="text-meta text-ink-meta">
            {t('quoteTemplate.versionOption', { values: { version: String(current.version) } })}
          </span>
        ) : null}
        {current && current.versions.length > 1 ? (
          <label className="flex items-center gap-2 text-meta text-ink-meta">
            {t('quoteTemplate.version')}
            <select
              data-template-version
              className="h-input rounded-control border border-control bg-surface px-2 text-dense text-ink"
              value={String(viewing ?? current.versions[0]?.version ?? current.version)}
              onChange={(event) => {
                const picked = Number(event.target.value);
                setViewing(picked === current.versions[0]?.version ? null : picked);
              }}
            >
              {current.versions.map((entry) => (
                <option key={entry.version} value={String(entry.version)}>
                  {t('quoteTemplate.versionOption', { values: { version: String(entry.version) } })}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <span className="ms-auto flex items-center gap-2">
          {editable ? (
            <Button intent="primary" data-template-save disabled={save.isPending} onClick={() => save.mutate()}>
              {t('quoteTemplate.save')}
            </Button>
          ) : null}
          {editable && !creating ? (
            <Button intent="ghost" data-template-archive disabled={archive.isPending} onClick={() => archive.mutate()}>
              {t('quoteTemplate.archive')}
            </Button>
          ) : null}
        </span>
      </header>

      {oldVersion ? (
        <p data-template-old-version className="rounded-control bg-sunken px-3 py-2 text-dense text-ink">
          {t('quoteTemplate.oldVersion', { values: { version: String(viewing) } })}
        </p>
      ) : null}
      {!manage ? (
        <p data-template-read-only className="text-meta text-ink-meta">
          {t('quoteTemplate.notPermitted')}
        </p>
      ) : null}
      {save.error ?? archive.error ? (
        <p data-template-refusal className="rounded-control bg-danger-tint px-3 py-2 text-dense text-danger-text">
          {errorText((save.error ?? archive.error)!)}
        </p>
      ) : null}
      {save.isSuccess && !save.isPending ? (
        <p data-template-saved className="text-meta text-success-text">
          {t('quoteTemplate.saved', { values: { version: String(save.data.version) } })}
        </p>
      ) : null}

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[60%_40%]">
        <Panel data-region="template-editor">
          <div className="flex flex-col gap-3">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              <label className="flex flex-col gap-1 text-dense text-ink-600 sm:col-span-3">
                {t('quoteTemplate.name')}
                <input data-template-name className={INPUT} value={name} disabled={!editable} onChange={(event) => setName(event.target.value)} />
              </label>
              <label className="flex flex-col gap-1 text-dense text-ink-600">
                {t('quoteTemplate.currency')}
                <input
                  data-template-currency
                  className={INPUT}
                  maxLength={3}
                  value={currency}
                  disabled={!editable}
                  onChange={(event) => setCurrency(event.target.value.toUpperCase())}
                />
              </label>
              <label className="flex flex-col gap-1 text-dense text-ink-600">
                {t('quoteTemplate.validityDays')}
                <input
                  data-template-validity
                  type="number"
                  min={1}
                  max={365}
                  className={INPUT}
                  value={validityDays}
                  disabled={!editable}
                  onChange={(event) => setValidityDays(event.target.value)}
                />
              </label>
              {/* A select with one option changes nothing. With no quote document
                  templates yet, say so and say where they are made. */}
              {editable && (documents.data?.templates.length ?? 0) === 0 && !current?.documentTemplateId ? (
                <p data-template-no-documents className="flex flex-col gap-1 text-dense text-ink-600">
                  {t('quoteTemplate.branding')}
                  <span className="text-meta text-ink-meta">{t('quoteTemplate.noDocuments')}</span>
                </p>
              ) : (
                <label className="flex flex-col gap-1 text-dense text-ink-600">
                  {t('quoteTemplate.branding')}
                  <select
                    data-template-document
                    className={INPUT}
                    value={documentTemplateId}
                    disabled={!editable}
                    onChange={(event) => setDocumentTemplateId(event.target.value)}
                  >
                    <option value="">{t('quoteTemplate.brandingNone')}</option>
                    {(documents.data?.templates ?? []).map((document) => (
                      <option key={document.templateId} value={document.templateId}>
                        {document.name}
                      </option>
                    ))}
                    {/* Kept visible when the reader cannot list document templates. */}
                    {current?.documentTemplateId && !documents.data ? (
                      <option value={current.documentTemplateId}>{t('quoteTemplate.brandingChosen')}</option>
                    ) : null}
                  </select>
                </label>
              )}
            </div>

            <SectionLabel>{t('quote.lines')}</SectionLabel>
            {editable ? (
              <div className="flex flex-col gap-1">
                <label className="flex flex-col gap-1 text-dense text-ink-600">
                  {t('quoteTemplate.searchProducts')}
                  <input
                    data-template-product-search
                    type="search"
                    className={INPUT}
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                  />
                </label>
                <label className="flex flex-col gap-1 text-dense text-ink-600">
                  {t('quoteTemplate.addProduct')}
                  <select
                    data-template-add-product
                    className={INPUT}
                    value={product}
                    onChange={(event) => addProduct(event.target.value)}
                  >
                    <option value="">{t('quoteTemplate.chooseProduct')}</option>
                    {(products.data?.products ?? [])
                      .filter((entry) => entry.currency === currency)
                      .map((entry) => (
                        <option key={entry.productId} value={entry.productId}>
                          {entry.name} · {entry.sku}
                        </option>
                      ))}
                  </select>
                </label>
                {(products.data?.products.length ?? 0) >= 25 ? (
                  <p data-template-products-capped className="text-meta text-ink-meta">
                    {t('quoteTemplate.productsCapped')}
                  </p>
                ) : null}
                <p className="text-meta text-ink-meta">{t('quoteTemplate.productPricing', { values: { currency } })}</p>
              </div>
            ) : null}
            <LineItemGrid
              lines={lines}
              editable={editable}
              discountPercent="0"
              money={money}
              onChange={setLines}
              labels={{
                description: t('quote.description'),
                quantity: t('quote.quantity'),
                unitPrice: t('quote.unitPrice'),
                discount: t('quote.discount'),
                lineTotal: t('quote.lineTotal'),
                optional: t('quote.optional'),
                addLine: t('quote.addLine'),
                removeLine: (description) => t('quote.removeLine', { values: { description } }),
                keyboardHint: t('quote.keyboardHint'),
                subtotal: t('quote.subtotal'),
                optionalTotal: t('quote.optionalTotal'),
                discountLabel: t('quote.discountLabel'),
                total: t('quote.total'),
                readOnly: t('quoteTemplate.readOnly'),
                rowActions: t('quote.rowActions'),
              }}
            />

            <label className="flex flex-col gap-1 text-dense text-ink-600">
              {t('quote.terms')}
              <textarea
                data-template-terms
                rows={5}
                className="rounded-control border border-control bg-surface px-3 py-2 text-body text-ink"
                value={terms}
                disabled={!editable}
                onChange={(event) => setTerms(event.target.value)}
              />
            </label>
          </div>
        </Panel>

        {/* The quote this template starts, as the customer would read it. */}
        <Panel data-region="template-preview">
          <article className="flex flex-col gap-3">
            <SectionLabel>{t('quote.preview')}</SectionLabel>
            <h2 className="serif-moment text-h2 text-ink">{name || t('quoteTemplate.new')}</h2>
            <p data-template-preview-validity className="text-meta text-ink-meta">
              {t('quoteTemplate.validFor', { values: { days: validityDays } })}
            </p>
            <ul className="flex flex-col">
              {lines
                .filter((line) => !line.optional)
                .map((line, index) => (
                  <li key={`main-${String(index)}`} className="flex items-baseline justify-between gap-3 border-t border-t-rule py-1 first:border-t-0">
                    <span className="min-w-0 flex-1 truncate text-dense text-ink">{line.description || '—'}</span>
                    <Num align="end" className="text-dense text-ink">
                      {money(lineTotalOf(line))}
                    </Num>
                  </li>
                ))}
            </ul>
            {lines.some((line) => line.optional) ? (
              <section data-template-preview-optional className="flex flex-col gap-1 rounded-card bg-sunken p-2">
                <span className="text-meta text-ink-meta">{t('quoteTemplate.optionalSection')}</span>
                {lines
                  .filter((line) => line.optional)
                  .map((line, index) => (
                    <p key={`optional-${String(index)}`} className="flex items-baseline justify-between gap-3 text-dense text-ink">
                      <span className="min-w-0 flex-1 truncate">{line.description || '—'}</span>
                      <Num align="end">{money(lineTotalOf(line))}</Num>
                    </p>
                  ))}
              </section>
            ) : null}
            <p className="flex items-baseline justify-between gap-3 border-t border-t-rule-strong pt-2">
              <span className="text-body font-medium text-ink">{t('quote.total')}</span>
              <Num align="end" data-template-preview-total className="text-body font-medium text-ink">
                {money(totals.total)}
              </Num>
            </p>
            {terms.trim() ? <p className="whitespace-pre-wrap text-meta text-ink-meta">{terms}</p> : null}
          </article>
        </Panel>
      </div>
    </div>
  );
}

function QuoteTemplateLoading(): JSX.Element {
  const t = useT();
  return (
    <div className="flex flex-col gap-4" role="status" aria-busy>
      <span className="sr-only">{t('state.loading')}</span>
      <Skeleton className="h-line-h2 w-panel-wide" />
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[60%_40%]">
        <Skeleton className="h-plot w-full" />
        <Skeleton className="h-plot w-full" />
      </div>
    </div>
  );
}

function QuoteTemplateError(): JSX.Element {
  const t = useT();
  return <ErrorState title={t('state.error')} body={t('quoteTemplate.loadFailed')} />;
}

function QuoteTemplateEmpty(): JSX.Element {
  const t = useT();
  return <p className="text-body text-ink-meta">{t('quoteTemplate.emptyState')}</p>;
}

export const screen = defineScreen({
  id: 'SC-43',
  route: '/quote-templates/:id',
  surface: 'page',
  title: 'Quotation template builder',
  features: ['F-158'],
  entitlement: 'F-158',
  reachedBy: [{ kind: 'screen', from: 'SC-30', how: 'a quotation template in Setup' }],
  components: [],
  elements: ['QuoteTemplateBuilder', 'LineItemGrid'],
  sampleRecord: [{ table: { name: 'quote_template', idColumn: 'template_id' }, param: 'id' }],
  states: { empty: QuoteTemplateEmpty, loading: QuoteTemplateLoading, error: QuoteTemplateError },
  component: QuoteTemplateBuilderScreen,
});

export default screen;
